import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Button, Row, Spinner } from "@narmi/design_system";
import { FileUploader } from "cerulean";
import { entities, useStaggeredCount } from "byzantine";
import styles from "./InvoiceContainer.module.scss";
import PreviewCard from "./PreviewCard";
import RecipientStep from "./RecipientStep";
import { LOOKING_FOR_ITEMS } from "./constants";
import { useInvoiceFlow } from "./InvoiceFlowContext";

const UploadStep = () => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const {
    uploadedFileName,
    previewUrl,
    previewMimeType,
    previewPages,
    completedCount,
    requestDone,
    handleFileNameChange: onFileNameChange,
    handleSendRequest: onSendRequest,
    clearUploadedFile: onRemove,
  } = useInvoiceFlow();

  const onCancel = () => navigate("/transfer");

  const hasFile = Boolean(previewUrl);
  const requestStarted = requestDone !== null;
  // The backend doesn't pace its phases — `matching_recipient` is short enough
  // that a poll rarely sees it, so the raw count can jump two rows at once.
  // Reveal earned rows one at a time; this is clamped to the real count, so it
  // never shows progress the backend hasn't reported.
  const visibleCompletedCount = useStaggeredCount(
    completedCount,
    LOOKING_FOR_ITEMS.length,
    entities.invoices.INVOICE_SCANNING_ROW_STAGGER_MS
  );
  const checkmarksDone = visibleCompletedCount === LOOKING_FOR_ITEMS.length;
  const isRecipientVisible = checkmarksDone && requestDone === true;

  // timed from when the last row starts spinning, not from upload
  const isLastRowPending =
    requestStarted && visibleCompletedCount === LOOKING_FOR_ITEMS.length - 1;
  const [showStillWorking, setShowStillWorking] = useState(false);
  useEffect(() => {
    if (!isLastRowPending) {
      setShowStillWorking(false);
      return undefined;
    }
    const timer = setTimeout(
      () => setShowStillWorking(true),
      entities.invoices.INVOICE_STILL_PROCESSING_DELAY_MS
    );
    return () => clearTimeout(timer);
  }, [isLastRowPending]);

  return (
    <div className={styles.invoiceUploaderContainer}>
      <div className={styles.uploaderColumn}>
        {hasFile ? (
          <PreviewCard
            previewUrl={previewUrl}
            // once scanning produces page renders, swap the local blob (or the
            // PDF placeholder) for the real image
            previewPages={previewPages}
            previewMimeType={previewMimeType}
            fileName={uploadedFileName}
            onRemove={onRemove}
            showZoomControls={isRecipientVisible}
          />
        ) : (
          <div className={styles.uploaderCard}>
            <FileUploader
              expand
              value={uploadedFileName}
              onChange={onFileNameChange}
              sendRequest={onSendRequest}
              shouldSuppressErrorNotification={() => true}
            />
          </div>
        )}
      </div>

      {isRecipientVisible ? (
        <RecipientStep />
      ) : (
        <div className={styles.uploadControlsColumn}>
          <div className={styles.detailsColumn}>
            <h1 className="fontFamily--heading fontSize--heading1">
              {l10n.getString("invoice-upload-title")}
            </h1>
            <p className="fontColor--secondary margin--top--s">
              {l10n.getString("invoice-upload-description")}
            </p>

            <p
              className={`fontColor--secondary fontWeight--bold fontSize--xs margin--top--xl ${styles.lookingForLabel}`}
            >
              {l10n.getString("invoice-upload-looking-for")}
            </p>

            <ul className={styles.listContainer}>
              {LOOKING_FOR_ITEMS.map((id, index) => {
                const isComplete =
                  index < visibleCompletedCount || showStillWorking;
                // an item spins once the upload has started and it's next in line
                const isLoading =
                  requestStarted &&
                  !isComplete &&
                  index === visibleCompletedCount;

                let icon = <span className={styles.idleSpinner} />;
                if (isComplete) {
                  icon = (
                    <span className={styles.greenCheck}>
                      <span className="narmi-icon-check" aria-hidden="true" />
                    </span>
                  );
                } else if (isLoading) {
                  icon = <Spinner size={32} color="var(--color-successDark)" />;
                }

                return (
                  <li key={id} className={styles.listItem}>
                    <span className={styles.itemIconContainer}>{icon}</span>
                    <span>{l10n.getString(id)}</span>
                  </li>
                );
              })}
            </ul>

            {showStillWorking && (
              <div className={styles.stillWorkingContainer}>
                <Spinner size={32} color="var(--color-successDark)" />
                <span className="fontColor--secondary">
                  {l10n.getString("invoice-upload-still-on-it")}
                </span>
              </div>
            )}
          </div>

          <div className={styles.actionsRow}>
            <Row alignItems="center" justifyContent="end">
              <Row.Item shrink>
                <Button
                  kind="plain"
                  label={l10n.getString("button-cancel")}
                  onClick={onCancel}
                />
              </Row.Item>
              <Row.Item shrink>
                <Button label={l10n.getString("button-next")} disabled />
              </Row.Item>
            </Row>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadStep;
